import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Animated, { useAnimatedStyle, withTiming, } from 'react-native-reanimated';
import Svg, { Line } from 'react-native-svg';
import { rem, Spacer } from 'design-system';
import FlatListItemCard from 'design-system/FlatListItemCard';
import Text, { styles as textStyles } from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { CustomSplitType } from 'utils/types/navigation';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { IconArrow } from 'design-system/icons';
const customSplitTypes = [
    CustomSplitType.AMOUNT,
    CustomSplitType.PERCENTAGE,
    CustomSplitType.SHARE,
];
export const CustomSplitHeader = ({ selectedType, setSelectedType, users, split, }) => {
    const styles = useStyles(styleSet);
    const formattedUsers = useMemo(() => {
        let firstSplitIndex = users.length - 1;
        let lastSplitIndex = 0;
        const activeSplitIndexes = [];
        for (let i = 0; i < users.length; i++) {
            if (Number(split[users[i].id]) > 0) {
                activeSplitIndexes.push(i);
                if (i < firstSplitIndex) {
                    firstSplitIndex = i;
                }
                if (i > lastSplitIndex) {
                    lastSplitIndex = i;
                }
            }
        }
        if (!activeSplitIndexes.length)
            return null;
        return users.map((u, i) => ({
            ...u,
            splitPercentage: `${Number(split[u.id])}%`,
            isFirstSplitIndex: i === firstSplitIndex,
            isLastSplitIndex: i === lastSplitIndex,
            showSeparatorMarker: activeSplitIndexes.indexOf(i) > 0,
        }));
    }, [users, split]);
    const animatedStyles = useAnimatedStyle(() => ({
        height: withTiming(formattedUsers ? 88 : 0, { duration: 200 }),
        flex: 1,
        overflow: 'hidden',
    }), [formattedUsers]);
    return (<>
      <View style={styles.types}>
        <SelectedPill selectedType={selectedType}/>
        {customSplitTypes.map((splitType) => (<TouchableBounce key={splitType} style={styles.typePill} onPress={() => setSelectedType(splitType)}>
            <Text Text-14 centered {...(selectedType === splitType
            ? { OnBrand: true }
            : { Secondary: true })}>
              {splitType}
            </Text>
          </TouchableBounce>))}
      </View>
      <Spacer.H16 />
      <Animated.View style={animatedStyles}>
        <FlatListItemCard index={0} style={[styles.card]}>
          <View style={{
            flexDirection: 'row',
            flex: 1,
        }}>
            {formattedUsers?.map(({ id, firstName, handle, splitPercentage, isFirstSplitIndex, isLastSplitIndex, showSeparatorMarker, }, i) => (<AnimatedSplitBar key={id} name={firstName || handle || ''} splitPercentage={splitPercentage} index={i} isFirstSplitIndex={isFirstSplitIndex} isLastSplitIndex={isLastSplitIndex} showSeparatorMarker={showSeparatorMarker}/>))}
          </View>
        </FlatListItemCard>
      </Animated.View>
    </>);
};
const styleSet = createStyleSheets((colors) => ({
    types: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: colors.cards.onDark,
        marginHorizontal: rem(16),
        padding: rem(4),
        borderRadius: rem(50),
        height: rem(42),
        marginTop: rem(8),
    },
    typePill: {
        flex: 1,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: rem(8),
    },
    card: {
        backgroundColor: colors.cards.onDark,
        paddingHorizontal: rem(16),
        paddingTop: rem(16),
        paddingBottom: rem(42),
        overflow: 'hidden',
        height: rem(88),
    },
    textInput: {
        ...textStyles.Numbers_20,
        color: colors.text.primary,
    },
}));
const DashedSvg = ({ height }) => {
    const colors = useColors();
    return (<Svg height={height} width="1">
      <Line x1="0" y1={height} x2="0" y2="0" stroke={colors.elements.secondary} strokeWidth="2" strokeDasharray="4, 2"/>
    </Svg>);
};
const characterWidth = rem(10);
const AnimatedSplitBar = ({ name, splitPercentage, index, isFirstSplitIndex, isLastSplitIndex, showSeparatorMarker, }) => {
    const colors = useColors();
    const animatedStyles = useAnimatedStyle(() => ({
        flexBasis: withTiming(splitPercentage),
    }));
    const [hideName, setHideName] = useState(false);
    const barStyle = useMemo(() => ({
        backgroundColor: index % 2 === 0
            ? colors.elements.brandLight
            : colors.elements.brandMedium,
        height: rem(8),
        width: '100%',
        borderRadius: isFirstSplitIndex ? 99 : 0,
        borderTopRightRadius: isLastSplitIndex ? 99 : 0,
        borderBottomLeftRadius: isFirstSplitIndex ? 99 : 0,
        borderBottomRightRadius: isLastSplitIndex ? 99 : 0,
    }), [
        colors.elements.brandLight,
        colors.elements.brandMedium,
        index,
        isFirstSplitIndex,
        isLastSplitIndex,
    ]);
    return (<Animated.View style={animatedStyles}>
      <View style={splitBarStyles.nameContainer}>
        <Text Text-12 numberOfLines={1} centered style={{ opacity: hideName ? 0 : 1 }} onLayout={(e) => setHideName(e.nativeEvent.layout.width < characterWidth)}>
          {name}
        </Text>
      </View>
      <View style={barStyle}/>
      {showSeparatorMarker ? (<View style={splitBarStyles.separatorMarker}>
          <DashedSvg height={52}/>
          <IconArrow width={8} direction="up" color={colors.elements.brand}/>
        </View>) : null}
    </Animated.View>);
};
const splitBarStyles = StyleSheet.create({
    nameContainer: {
        overflow: 'hidden',
        width: '100%',
        paddingHorizontal: rem(4),
        marginBottom: rem(4),
    },
    separatorMarker: {
        position: 'absolute',
        left: -4,
        alignItems: 'center',
        gap: 8.5,
    },
});
const SelectedPill = ({ selectedType }) => {
    const styles = useStyles(selectedPillStyles);
    const { width } = useAppFrameDimensions();
    const selectedIndex = useMemo(() => customSplitTypes.indexOf(selectedType), [selectedType]);
    const divWidth = useMemo(() => (width - 40) / 3, [width]);
    const animatedStyles = useAnimatedStyle(() => ({
        left: withTiming(selectedIndex * divWidth + 4),
        width: divWidth,
    }), [divWidth, selectedIndex]);
    return (<Animated.View style={[animatedStyles, styles.absView]}>
      <View style={styles.marker}/>
    </Animated.View>);
};
const selectedPillStyles = createStyleSheets((colors) => ({
    absView: {
        top: rem(4),
        bottom: rem(4),
        position: 'absolute',
    },
    marker: {
        height: '100%',
        width: '100%',
        borderRadius: rem(50),
        backgroundColor: colors.buttons.brandLight,
    },
}));
